import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { EASE } from "@/components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ENQUIRY_TYPES = ["New Business", "Product Purchase", "Career", "Media", "Other"];

const emptyForm = {
  enquiry_type: "",
  name: "",
  company: "",
  email: "",
  phone: "",
  message: "",
};

const ContactPage = () => {
  const [searchParams] = useSearchParams();
  const presetType = searchParams.get("type");
  const [form, setForm] = useState({
    ...emptyForm,
    enquiry_type: ENQUIRY_TYPES.includes(presetType) ? presetType : "",
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const setField = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.enquiry_type) next.enquiry_type = "Please select an enquiry type.";
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) next.email = "Please enter your email.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim()))
      next.email = "Please enter a valid email.";
    if (!form.message.trim()) next.message = "Please enter a message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          enquiry_type: form.enquiry_type,
          name: form.name.trim(),
          company: form.company.trim() || null,
          email: form.email.trim(),
          phone: form.phone.trim() || null,
          message: form.message.trim(),
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      setForm(emptyForm);
    } catch (err) {
      setStatus("error");
    }
  };

  const inputBase =
    "w-full bg-transparent border-b border-[var(--cerra-border)] py-3 text-base sm:text-lg text-[var(--cerra-fg)] placeholder-black/30 focus:outline-none focus:border-[var(--orange)] transition-colors duration-300";

  const Label = ({ children, required }) => (
    <span className="block text-xs font-bold uppercase tracking-[0.22em] text-black/40 mb-1">
      {children}
      {required && <span className="text-[var(--orange)]"> *</span>}
    </span>
  );

  const FieldError = ({ name }) =>
    errors[name] ? (
      <p data-testid={`error-${name}`} className="mt-2 text-xs font-medium text-[var(--orange)]">
        {errors[name]}
      </p>
    ) : null;

  return (
    <div data-testid="contact-page" className="bg-white min-h-screen">
      <Navbar />
      <section id="contact-form" className="pt-36 sm:pt-44 pb-24 sm:pb-32 px-5 sm:px-8 scroll-mt-24">
        <div className="mx-auto max-w-[1600px]">
          {/* Header */}
          <div className="border-b border-[var(--cerra-border)] pb-10 mb-16">
            <span className="text-[var(--orange)] text-xs font-bold uppercase tracking-[0.28em]">
              ✦ Contact
            </span>
            <h1 className="mt-5 font-head text-4xl sm:text-6xl lg:text-7xl font-medium uppercase kern-tight leading-none">
              Start the conversation.
            </h1>
          </div>

          <div className="grid lg:grid-cols-12 gap-x-16 gap-y-16">
            {/* Intro / left */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE }}
              className="lg:col-span-5"
            >
              <p className="font-head text-xl sm:text-2xl font-medium leading-snug kern-tight">
                Cerra Design is ready to hear what you’re creating — or what’s
                slowing things down.
              </p>
              <p className="mt-6 text-base sm:text-lg leading-relaxed text-black/60">
                You don’t need a polished plan to begin. Start the conversation and
                we’ll move it forward together.
              </p>

              <div className="mt-12 space-y-6 border-t border-[var(--cerra-border)] pt-10">
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/40">Studio</p>
                  <address className="mt-3 not-italic text-sm leading-relaxed text-black/70">
                    Cerra Design HQ<br />
                    2/12 Aileen Avenue<br />
                    Heidelberg West, Australia 3081
                  </address>
                </div>
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-black/40">Direct</p>
                  <a
                    data-testid="contact-phone-link"
                    href="tel:+61407350408"
                    className="mt-3 block text-sm text-black/70 hover:text-[var(--orange)] transition-colors"
                  >
                    +61 (0) 407 350 408
                  </a>
                </div>
              </div>
            </motion.div>

            {/* Form / right */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
              className="lg:col-span-7"
            >
              {status === "success" ? (
                <div
                  data-testid="contact-success"
                  className="flex flex-col items-start gap-5 border border-[var(--cerra-border)] bg-neutral-50 p-10"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--orange)] text-white">
                    <Check size={24} />
                  </span>
                  <h3 className="font-head text-2xl sm:text-3xl font-medium uppercase kern-tight">
                    Message sent.
                  </h3>
                  <p className="text-base leading-relaxed text-black/60 max-w-md">
                    Thanks for reaching out — your enquiry has landed in our inbox.
                    We’ll be in touch shortly.
                  </p>
                  <button
                    data-testid="contact-send-another"
                    onClick={() => setStatus("idle")}
                    className="mt-2 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-[var(--cerra-fg)] hover:text-[var(--orange)] transition-colors"
                  >
                    Send another <ArrowUpRight size={15} />
                  </button>
                </div>
              ) : (
                <form data-testid="contact-form" onSubmit={handleSubmit} noValidate className="space-y-8">
                  <div>
                    <Label required>Enquiry Type</Label>
                    <select
                      data-testid="enquiry-type-select"
                      value={form.enquiry_type}
                      onChange={setField("enquiry_type")}
                      className={`${inputBase} appearance-none cursor-pointer`}
                    >
                      <option value="" disabled>
                        Select an option
                      </option>
                      {ENQUIRY_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                    <FieldError name="enquiry_type" />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <Label required>Name</Label>
                      <input
                        data-testid="name-input"
                        type="text"
                        value={form.name}
                        onChange={setField("name")}
                        placeholder="Your name"
                        className={inputBase}
                      />
                      <FieldError name="name" />
                    </div>
                    <div>
                      <Label>Company</Label>
                      <input
                        data-testid="company-input"
                        type="text"
                        value={form.company}
                        onChange={setField("company")}
                        placeholder="Company (optional)"
                        className={inputBase}
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-8">
                    <div>
                      <Label required>Email</Label>
                      <input
                        data-testid="email-input"
                        type="email"
                        value={form.email}
                        onChange={setField("email")}
                        placeholder="you@example.com"
                        className={inputBase}
                      />
                      <FieldError name="email" />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <input
                        data-testid="phone-input"
                        type="tel"
                        value={form.phone}
                        onChange={setField("phone")}
                        placeholder="Phone (optional)"
                        className={inputBase}
                      />
                    </div>
                  </div>

                  <div>
                    <Label required>Message</Label>
                    <textarea
                      data-testid="message-textarea"
                      value={form.message}
                      onChange={setField("message")}
                      rows={5}
                      placeholder="Tell us what you’re creating…"
                      className={`${inputBase} resize-none`}
                    />
                    <FieldError name="message" />
                  </div>

                  {status === "error" && (
                    <p data-testid="contact-error" className="text-sm font-medium text-red-600">
                      Something went wrong sending your message. Please try again.
                    </p>
                  )}

                  <button
                    data-testid="contact-submit-button"
                    type="submit"
                    disabled={status === "sending"}
                    className="inline-flex items-center gap-3 bg-[var(--orange)] hover:bg-[var(--orange-hover)] disabled:opacity-60 text-white text-xs font-bold uppercase tracking-[0.22em] px-10 py-5 transition-colors duration-300"
                  >
                    {status === "sending" ? (
                      <>
                        Sending <Loader2 size={16} className="animate-spin" />
                      </>
                    ) : (
                      <>
                        Send Enquiry <ArrowUpRight size={16} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default ContactPage;
